// NOVO DNA · biblioteca de componentes (padrão do Dashboard DNA aprovado).
import React from 'react';
import { AlertCircle, CheckCircle2, Info, Inbox, TriangleAlert } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { statusTone } from './status';

export { statusTone, textOf } from './status';

export const C = {
  ink: '#2A1240', aubergine: '#3F1F5C', purple: '#7B3F98', magenta: '#D6336C',
  green: '#1FA34A', leaf: '#7CC47F', lime: '#B9CC3A', orange: '#F15A29', amber: '#F7953A',
  muted: '#6B5E7B', line: '#EDE7F3', paper: '#FBF9FD',
};
export const DISPLAY = { fontFamily: 'Manrope, sans-serif' };

const BrandDots = () => (
  <svg className="pointer-events-none absolute right-0 top-0 h-full w-[360px] opacity-90" viewBox="0 0 420 220" aria-hidden>
    <circle cx="330" cy="70" r="78" fill={C.purple} opacity=".55" />
    <circle cx="395" cy="120" r="70" fill={C.orange} opacity=".5" />
    <circle cx="300" cy="160" r="58" fill={C.magenta} opacity=".45" />
    <circle cx="378" cy="30" r="46" fill={C.green} opacity=".55" />
    {[[220, 40, 6, C.leaf], [200, 90, 4, C.lime], [235, 150, 5, C.amber]].map(([x, y, r, f], i) => <circle key={i} cx={x} cy={y} r={r} fill={f} />)}
  </svg>
);

/** Topo de página. variant="hero" (faixa ink com marca) | "plain" (leve, para telas operacionais). */
export function PageHeader({ kicker = 'NOVO DNA', title, subtitle, actions, variant = 'plain', testid = 'page-header' }) {
  if (variant === 'hero') {
    return (
      <header className="relative overflow-hidden rounded-[28px] px-6 py-7 sm:px-9 sm:py-8 text-white" style={{ background: C.ink }} data-testid={testid}>
        <BrandDots />
        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            {kicker && <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/55">{kicker}</p>}
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={DISPLAY}>{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-white/65">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </header>
    );
  }
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-testid={testid}>
      <div className="min-w-0">
        {kicker && <p className="dna-kicker">{kicker}</p>}
        <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ ...DISPLAY, color: C.ink }}>{title}</h1>
        {subtitle && <p className="mt-1 text-sm" style={{ color: C.muted }}>{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Cabeçalho de módulo (Financeiro, Matrículas, Acadêmico, Secretaria…): faixa ink compacta + ações em vidro. */
export function ModuleHeader({ kicker, title, subtitle, icon: Icon, actions, children, testid = 'module-header' }) {
  return (
    <header className="dna-module-header relative overflow-hidden rounded-[24px] px-5 py-5 sm:px-7 sm:py-6 text-white" style={{ background: C.ink }} data-testid={testid}>
      <BrandDots />
      <div className="relative z-10 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-center gap-3.5">
          {Icon && <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15 sm:flex"><Icon className="h-6 w-6" /></span>}
          <div className="min-w-0">
            {kicker && <p className="text-[10.5px] font-bold uppercase tracking-[0.26em] text-white/55">{kicker}</p>}
            <h1 className="mt-0.5 text-2xl sm:text-3xl font-extrabold tracking-tight" style={DISPLAY}>{title}</h1>
            {subtitle && <p className="mt-1 text-[13px] text-white/65">{subtitle}</p>}
            {children}
          </div>
        </div>
        {actions && <div className="dna-mh-actions flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

const TONE_HEX = {
  green: C.green, emerald: C.green, teal: C.green, success: C.green, lime: C.lime,
  blue: C.purple, sky: C.purple, cyan: C.purple, indigo: C.purple, violet: C.purple, purple: C.purple, info: C.purple,
  amber: C.amber, yellow: C.amber, orange: C.orange, warning: C.amber,
  rose: C.magenta, pink: C.magenta, red: C.orange, danger: C.orange, fuchsia: C.magenta,
  slate: C.muted, gray: C.muted, neutral: C.muted,
};
/** Nome de cor legado (green/blue/rose…) → cor da marca. */
export const toneColor = (name) => TONE_HEX[name] || (String(name || '').startsWith('#') ? name : C.purple);

/** Bloco de conteúdo padrão. */
export const Panel = ({ title, kicker, children, testid, right, className = '' }) => (
  <section className={`min-w-0 rounded-[24px] border bg-white p-5 sm:p-6 ${className}`} style={{ borderColor: C.line }} data-testid={testid}>
    {(title || kicker || right) && (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          {kicker && <p className="dna-kicker">{kicker}</p>}
          {title && <h2 className="text-base md:text-lg font-extrabold tracking-tight" style={{ ...DISPLAY, color: C.ink }}>{title}</h2>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

/** Card KPI (clicável quando recebe onClick). */
export function KpiCard({ id, label, value, hint, icon: Icon, color = C.purple, onClick, index = 0 }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} data-testid={`kpi-${id}`}
      className="group relative min-w-0 overflow-hidden rounded-[22px] border bg-white p-5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(63,31,92,.45)] dna-rise"
      style={{ borderColor: C.line, animationDelay: `${index * 45}ms` }}>
      <span className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-[.13] transition-transform duration-300 group-hover:scale-125" style={{ background: color }} />
      {Icon && <span className="flex h-10 w-10 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}><Icon className="h-5 w-5" /></span>}
      <p className="mt-4 text-[28px] font-extrabold leading-none tracking-tight tabular-nums" style={{ ...DISPLAY, color: C.ink }} data-testid={`kpi-${id}-value`}>{value}</p>
      <p className="mt-1.5 text-[13px] font-semibold" style={{ color: C.aubergine }}>{label}</p>
      {hint && <p className="mt-0.5 line-clamp-2 break-words text-[11.5px]" style={{ color: C.muted }}>{hint}</p>}
    </Tag>
  );
}

export const KpiGrid = ({ children, cols = 4, testid = 'kpi-grid' }) => (
  <div className={`grid grid-cols-2 gap-3 ${cols >= 8 ? 'md:grid-cols-4 xl:grid-cols-8' : cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`} data-testid={testid}>{children}</div>
);

export const EmptyState = ({ title = 'Nada por aqui', children, icon: Icon = Inbox, testid = 'empty-state', action }) => (
  <div className="flex flex-col items-center rounded-2xl border border-dashed px-6 py-10 text-center" style={{ borderColor: C.line }} data-testid={testid}>
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${C.purple}12`, color: C.purple }}><Icon className="h-6 w-6" /></span>
    <p className="mt-3 text-sm font-bold" style={{ color: C.ink }}>{title}</p>
    {children && <p className="mt-1 max-w-md text-[13px]" style={{ color: C.muted }}>{children}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const TONES = {
  success: { bg: '#1FA34A14', fg: '#178A3D', icon: CheckCircle2 },
  warning: { bg: '#F7953A1A', fg: '#B5611A', icon: TriangleAlert },
  danger: { bg: '#E2412B14', fg: '#C2341F', icon: AlertCircle },
  info: { bg: '#7B3F9812', fg: '#5E2F7A', icon: Info },
  neutral: { bg: '#6B5E7B12', fg: '#6B5E7B', icon: Info },
};

/** Badge de status padronizado. */
export const StatusBadge = ({ tone, children, testid }) => (
  <span data-tone={tone || statusTone(children) || 'neutral'} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ background: TONES[tone || statusTone(children) || 'neutral'].bg, color: TONES[tone || statusTone(children) || 'neutral'].fg }} data-testid={testid}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'currentColor' }} />{children}
  </span>
);

/** Mensagem de sucesso/erro/aviso inline. */
export const Notice = ({ tone = 'info', title, children, testid = 'notice' }) => {
  const t = TONES[tone]; const Icon = t.icon;
  return (
    <div className="flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm" style={{ background: t.bg, color: t.fg }} data-testid={testid} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{title && <p className="font-bold">{title}</p>}{children && <div className="opacity-90">{children}</div>}</div>
    </div>
  );
};

/** Filtro em pílula (dark=true dentro de PageHeader hero). */
export const FilterSelect = ({ id, value, onChange, items, placeholder, dark = false }) => (
  <Select value={value != null && value !== '' ? String(value) : undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={`filter-${id}`}
      className={`h-10 w-auto min-w-[150px] gap-2 rounded-full ${dark ? 'border-white/15 bg-white/10 text-white backdrop-blur-md' : 'bg-white'}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {items.map((i) => <SelectItem key={i.value} value={String(i.value)} data-testid={`filter-${id}-${i.value}`}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

/** Moldura padrão para tabelas/listagens. */
export const TableShell = ({ title, toolbar, children, footer, testid = 'table-shell' }) => (
  <section className="min-w-0 overflow-hidden rounded-[24px] border bg-white" style={{ borderColor: C.line }} data-testid={testid}>
    {(title || toolbar) && (
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4" style={{ borderColor: C.line }}>
        {title && <h2 className="text-base font-extrabold" style={{ ...DISPLAY, color: C.ink }}>{title}</h2>}
        {toolbar && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
      </div>
    )}
    <div className="overflow-x-auto">{children}</div>
    {footer && <div className="border-t px-5 py-3 text-[12px]" style={{ borderColor: C.line, color: C.muted }}>{footer}</div>}
  </section>
);

/** Layout padrão de página: espaçamento e largura únicos em todos os portais. */
export const PageShell = ({ children, testid = 'page-shell', className = '' }) => (
  <div className={`mx-auto flex w-full max-w-[1600px] flex-col gap-5 pb-10 ${className}`} style={{ color: C.ink }} data-testid={testid}>{children}</div>
);
